import { createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { DateTime } from 'luxon';
import { apiGateway } from '../../../../config';

import type { AxiosResponse } from 'axios';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { AppThunk } from '../../../../redux/store';
import type { ErrorResponse, Factory, MailTarget, Port, ShippingSchedule, Ship } from 'src/@types/common';
import type {
  // ShipState,
  ModalMode,
  ShipSet,
  CreateShippingScheduleParams,
  UpdateShippingScheduleParams
} from '../../../../@types/ship';

// ----------------------------------------------------------------------

export interface ShipState {
  isLoading: boolean;
  hasError: boolean;
  hasSearched: boolean;
  isModalOpen: boolean;
  modalMode: ModalMode | undefined;
  message: string | undefined;
  options: {
    sourcePort: Port[];
    destinationPort: Port[];
    mailTarget: MailTarget[];
  };
  shipList: Ship[] | undefined;
  shipListPrev: Ship[] | undefined;
  factories: Factory[];
  ship: ShippingSchedule | CreateShippingScheduleParams | undefined;
  dateFrom: string | undefined;
  dateTo: string | undefined;
}

const initialState: ShipState = {
  isLoading: false,
  hasError: false,
  hasSearched: false,
  isModalOpen: false,
  modalMode: undefined,
  message: undefined,
  options: {
    sourcePort: [],
    destinationPort: [],
    mailTarget: [],
  },
  shipList: undefined,
  shipListPrev: undefined,
  factories: [],
  ship: undefined,
  dateFrom: DateTime.now().startOf('month').minus({months: 2}).toISO(),
  dateTo: DateTime.now().endOf('month').toISO(),
};

const slice = createSlice({
  name: 'masterShip',
  initialState,
  reducers: {
    startLoading(state: ShipState) {
      state.isLoading = true;
    },
    hasError(state: ShipState, action: PayloadAction<string>) {
      state.isLoading = false;
      state.hasError = true;
      state.message = action.payload;
    },
    setModalMode(state: ShipState, action: PayloadAction<ModalMode | undefined>) {
      state.modalMode = action.payload;
    },

    openModal(state: ShipState, action: PayloadAction<boolean>) {
      state.isModalOpen = action.payload;
    },
    showMessage(state: ShipState, action: PayloadAction<string>) {
      state.isLoading = false;
      state.hasError = false;
      state.message = action.payload;
    },
// ----------------------------------------------------------------------
    setFactories(state: ShipState, action: PayloadAction<Factory[]>) {
      state.isLoading = false;
      state.hasError = false;
      state.factories = action.payload;
    },
    setShip(state: ShipState, action: PayloadAction<ShippingSchedule>) {
      state.isLoading = false;
      state.hasError = false;
      state.ship = action.payload;
    },
// ----------------------------------------------------------------------
    getShipListSuccess(state: ShipState, action: PayloadAction<ShipSet>) {
      state.isLoading = false;
      state.hasError = false;
      state.message = undefined;
      state.shipList = action.payload.ship;
      state.shipListPrev = action.payload.shipPrev;
    },
    getOptionsPortSuccess(state: ShipState, action: PayloadAction<Port[]>) {
      state.isLoading = false;
      const allPorts: Port[] = action.payload;
      state.options.sourcePort = allPorts.filter(port => port.isSourcePort === true).filter(port => port.code !== 'KZU');
      state.options.destinationPort = allPorts.filter(port => port.isDestinationPort === true);
    },
    getOptionsMailTargetSuccess(state: ShipState, action: PayloadAction<MailTarget[]>) {
      state.isLoading = false;
      state.options.mailTarget = action.payload;
    },

// ----------------------------------------------------------------------
    createNewShip(state: ShipState) {
      const newShip: CreateShippingScheduleParams = {
        bookingNumber: '',
        shippingName: '',
        shippingCompanyCode: '',
        cutDate: DateTime.fromJSDate(new Date()).toUTC().toString(),
        cyOpenDate: DateTime.fromJSDate(new Date()).toUTC().toString(),
        etaDate: DateTime.fromJSDate(new Date()).toUTC().toString(),
        etdDate: DateTime.fromJSDate(new Date()).toUTC().toString(),
        originPortCode:'',
        destinationPortCode:'',
        isShipped: false,
        isDeleted: false,
        containerNumber: 0,
      };
      state.ship = newShip;
    },
// ----------------------------------------------------------------------
    changeStartDate(state: ShipState, action: PayloadAction<string | undefined>) {
      state.dateFrom = action.payload;
    },
    changeEndDate(state: ShipState, action: PayloadAction<string | undefined>) {
      state.dateTo = action.payload;
    },
// ----------------------------------------------------------------------
    resetMessage(state: ShipState) {
      state.isLoading = false;
      state.hasError = false;
      state.message = '';
    },
  },
});
  
export default slice.reducer;

// ----------------------------------------------------------------------
 
export function showError(message: string): AppThunk {
  return (dispatch): void => {
    dispatch(slice.actions.hasError(message));
  }
};

export function setModalMode(mode?: ModalMode): AppThunk {
  return (dispatch): void => {
    dispatch(slice.actions.setModalMode(mode));
  }
};

export function openModal(open: boolean): AppThunk {
  return (dispatch): void => {
    dispatch(slice.actions.openModal(open));
  }
};

export function createNewShip(): AppThunk {
  return (dispatch): void => {
    dispatch(slice.actions.createNewShip());
  }
};

export function getOptionsMailTarget(): AppThunk {
  return async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const response: AxiosResponse = await axios.get<MailTarget[]>(
        apiGateway + '/options/mailTarget',
        {
          headers: {
            'Authorization': 'Bearer ' + token,
          },
          timeout: 60000
        }
      );
      dispatch(slice.actions.getOptionsMailTargetSuccess(response.data));
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // error: AxiosError
        if (error.response) {
          const serverResponse: ErrorResponse = error.response.data;
          dispatch(slice.actions.hasError(serverResponse.message));
        } else {
          dispatch(slice.actions.hasError(error.message));
        }
      } else {
        if (error instanceof Error) {
          // error: Error
          dispatch(slice.actions.hasError(error.message));
        } else {
          const message = 'Unknown error';
          dispatch(slice.actions.hasError(message));
        }
      }
    }
  };
};

export function getOptionsPort(): AppThunk {
  return async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const response: AxiosResponse = await axios.get<Port[]>(
        apiGateway + '/options/port',
        {
          headers: {
            'Authorization': 'Bearer ' + token,
          },
          timeout: 60000
        }
      );
      dispatch(slice.actions.getOptionsPortSuccess(response.data));
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // error: AxiosError
        if (error.response) {
          const serverResponse: ErrorResponse = error.response.data;
          dispatch(slice.actions.hasError(serverResponse.message));
        } else {
          dispatch(slice.actions.hasError(error.message));
        }
      } else {
        if (error instanceof Error) {
          // error: Error
          dispatch(slice.actions.hasError(error.message));
        } else {
          const message = 'Unknown error';
          dispatch(slice.actions.hasError(message));
        }
      }
    }
  };
};

export function getShip(id: string): AppThunk {
  return async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const response: AxiosResponse = await axios.get<ShippingSchedule>(
        apiGateway + '/shipping/' + id,
        {
          headers: {
            'Authorization': 'Bearer ' + token,
          },
          timeout: 60000
        }
      );
      dispatch(slice.actions.setShip(response.data));
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // error: AxiosError
        if (error.response) {
          const serverResponse: ErrorResponse = error.response.data;
          dispatch(slice.actions.hasError(serverResponse.message));
        } else {
          dispatch(slice.actions.hasError(error.message));
        }
      } else {
        if (error instanceof Error) {
          // error: Error
          dispatch(slice.actions.hasError(error.message));
        } else {
          const message = 'Unknown error';
          dispatch(slice.actions.hasError(message));
        }
      }
    }
  }
}

export function getShipList(dateFrom?: string, dateTo?: string, limit?: number): AppThunk {
  return async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const response: AxiosResponse = await axios.get<ShipSet>(
        apiGateway + '/shipping/search', {
          headers: {
            Authorization: 'Bearer ' + token,
          },
          params: {
            dateFrom,
            dateTo
          },
          timeout: 60000,
          }
      );
      dispatch(slice.actions.getShipListSuccess(response.data));
    } catch (error) {
      // TODO:以下のエラー処理を有効化
      if (axios.isAxiosError(error)) {
        // error: AxiosError
        if (error.response) {
          const serverResponse: ErrorResponse = error.response.data;
          dispatch(slice.actions.hasError(serverResponse.message));
        } else {
          dispatch(slice.actions.hasError(error.message));
        }
      } else {
        if (error instanceof Error) {
          // error: Error
          dispatch(slice.actions.hasError(error.message));
        } else {
          const message = 'Unknown error';
          dispatch(slice.actions.hasError(message));
        }
      }
    }
  };
}

export function changeStartDate(date: string | undefined): AppThunk {
  return (dispatch): void => {
    dispatch(slice.actions.changeStartDate(date));
  }
};

export function changeEndDate(date: string | undefined): AppThunk {
  return (dispatch): void => {
    dispatch(slice.actions.changeEndDate(date));
  }
};

export function resetMessage(): AppThunk {
  return (dispatch): void => {
    dispatch(slice.actions.resetMessage());
  };
}

export function createShip(params: CreateShippingScheduleParams): AppThunk {
  return async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());

    const token = (await fetchAuthSession()).tokens?.idToken?.toString();
    const response: AxiosResponse = await axios.post<ShippingSchedule>(apiGateway + '/shipping', params, {
      headers: {
        Authorization: 'Bearer ' + token,
      },
      timeout: 60000,
    });

    // console.log('🏰response', response);
    dispatch(slice.actions.showMessage('successfully created'));
  }
}

export function updateShip(id: string, params: UpdateShippingScheduleParams): AppThunk {
  return async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());

    const token = (await fetchAuthSession()).tokens?.idToken?.toString();
    const response: AxiosResponse = await axios.put<ShippingSchedule>(
      apiGateway + '/shipping/'+ id,
      params,
      {
        headers: {
          Authorization: 'Bearer ' + token,
        },
        timeout: 60000,
      }
    );

    // console.log('🏰response', response);
    dispatch(slice.actions.showMessage('successfully updated'));
  }
}

export function deleteShip(id: string): AppThunk {
  return async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());

    const token = (await fetchAuthSession()).tokens?.idToken?.toString();
    const response: AxiosResponse = await axios.delete<ShippingSchedule>(
      apiGateway + '/shipping/'+ id,
      {
        headers: {
          Authorization: 'Bearer ' + token,
        },
        timeout: 60000,
      }
    );
    
    // console.log('🏰response', response);
    dispatch(slice.actions.showMessage('successfully deleted'));
  }
}